import { isDefined, makeValuesPredicate } from '@sgme/fp';
import type { QuoteInfo } from 'apiConnector/scheduledRun/model';
import { uniqBy } from 'lodash-es';
import type { FieldMetadata } from 'models/forms/fields';
import type { BatchFeesAndMetricsDataSet } from 'state/batchForm';
import {
  isFieldMetadataUsableInMultipleApply,
  mergeFieldsMetadata,
  modifyFieldTypeBasedOnId,
} from 'state/batchForm/multipleApply';
import type { FieldMetadataById } from 'state/common-form/metaData/fields/fields.model';
import {
  getAllSelectedFormsMatching,
  getBatchFormFieldMetadata,
  getBatchFormMustForceAdvancedMode,
  getBatchProductSubType,
  getBatchProductType,
  getBatchQuoteDetails,
  getBatchQuoteResult,
  getFormEnrichedProductSubType,
  getFormEnrichedProductType,
  getFormIdsInBatchTab,
  getMergedProductDescription,
  getMultipleBatchApplySelectedFieldsIds,
  getSelectedFormIdsInBatchTab,
  getUserPreferencesBatchWorkspaceVersion,
} from 'state/selectors';
import type { AppState } from 'state/state.model';
import { distinct } from 'utils/array/array';

export * from './infos/infos.selectors';
export * from './metaData/fields/fields.selectors';
export * from './quoteDetails/quoteDetails.selectors';

export function getBatchFeesAndMetricsIsExpanded(state: AppState) {
  return state.batchForm.feesAndMetrics.isExpanded;
}

export function getBatchFeesAndMetricsDataSet(state: AppState): BatchFeesAndMetricsDataSet {
  return state.batchForm.feesAndMetrics.activeDataSet;
}

export function getSelectedFormsProductSubtypes(state: AppState, tabId: string) {
  const formsIds = getSelectedFormIdsInBatchTab(state, tabId);
  const productVariations = formsIds
    .map(formId => {
      return {
        productSubType: getFormEnrichedProductSubType(state, formId),
        productType: getFormEnrichedProductType(state, formId),
      };
    })
    .filter(distinct)
    .sort();
  return { formsIds, productVariations };
}
export function getMergedProductDescriptionByTabIdForSelectedForms(state: AppState, tabId: string) {
  const { formsIds, productVariations } = getSelectedFormsProductSubtypes(state, tabId);
  const formIdsInTab = getFormIdsInBatchTab(state, tabId);
  const allFieldsMetadata = formsIds.map(formId => getBatchFormFieldMetadata(state, formId));
  const batchWorkspaceVersion = getUserPreferencesBatchWorkspaceVersion(state);
  const mustForceAdvancedMode = getBatchFormMustForceAdvancedMode(state, formIdsInTab);
  return getMergedProductDescription(
    state,
    'Batch',
    productVariations,
    allFieldsMetadata,
    batchWorkspaceVersion,
    mustForceAdvancedMode,
  );
}
export function getMergedProductDescriptionByTabId(state: AppState, tabId: string) {
  const formIds = getFormIdsInBatchTab(state, tabId);
  const originalProductVariation = {
    productSubType: getBatchProductSubType(state, tabId)!,
    productType: getBatchProductType(state, tabId)!,
  };
  if (!originalProductVariation.productSubType || !originalProductVariation.productType) {
    return undefined;
  }
  const productVariations = formIds
    .map(formId => {
      return {
        productSubType: getFormEnrichedProductSubType(state, formId),
        productType: getFormEnrichedProductType(state, formId),
      };
    })
    .filter(distinct)
    .filter(
      value =>
        value.productSubType !== originalProductVariation.productSubType &&
        value.productType === originalProductVariation.productType,
    )
    .sort();
  const allFieldsMetadata = formIds.map(formId => getBatchFormFieldMetadata(state, formId));
  const sortedProductSubtypesByPriority = [originalProductVariation, ...productVariations];
  const batchWorkspaceVersion = getUserPreferencesBatchWorkspaceVersion(state);
  const formIdsInTab = getFormIdsInBatchTab(state, tabId);
  const mustForceAdvancedMode = getBatchFormMustForceAdvancedMode(state, formIdsInTab);
  return getMergedProductDescription(
    state,
    'Batch',
    sortedProductSubtypesByPriority,
    allFieldsMetadata,
    batchWorkspaceVersion,
    mustForceAdvancedMode,
  );
}

export function getMergedFieldsMetadataByTabId(state: AppState, tabId: string) {
  const { formsIds } = getSelectedFormsProductSubtypes(state, tabId);
  const shownProductNodes = getAllSelectedFormsProductNodesByTabId(state, tabId);
  return formsIds.reduce((acc: FieldMetadataById, formId: string) => {
    const fieldsMetadata = getBatchFormFieldMetadata(state, formId);
    const modifiedFieldsMetadata = applyNodeTypesToFieldsMetadata(fieldsMetadata, shownProductNodes);
    return Object.entries(modifiedFieldsMetadata)
      .filter(([, v]) => isFieldMetadataUsableInMultipleApply(v) && shownProductNodes?.some(node => node.id === v.id))
      .reduce((sanitizedAcc, [k, v]) => mergeFieldsMetadata(sanitizedAcc, k, v), acc);
  }, {} satisfies FieldMetadataById);
}

export const getAllSelectedFormsProductNodesByTabId = (state: AppState, tabId: string) =>
  uniqBy(
    getMergedProductDescriptionByTabIdForSelectedForms(state, tabId)?.flatMap(block => block.children ?? []) ?? [],
    node => node.id,
  );

export const getAllSelectedProductNodesByTabId = (state: AppState, tabId: string) => {
  const selectedFields = getMultipleBatchApplySelectedFieldsIds(state, tabId);
  const productsNodes = getAllSelectedFormsProductNodesByTabId(state, tabId);
  const productsNodesFiltered = productsNodes.filter(n => selectedFields.includes(n.id));
  return productsNodesFiltered.map(node => modifyFieldTypeBasedOnId(node, state, tabId));
};

export function applyNodeTypesToFieldsMetadata(fieldsMetadata: FieldMetadataById, nodes: any[]) {
  const modifiedFieldsMetadata: Record<string, FieldMetadata> = {};

  nodes.forEach(node => {
    const fieldMetadata = fieldsMetadata[node.id];
    if (fieldMetadata) {
      if (node.type) {
        modifiedFieldsMetadata[node.id] = {
          ...fieldMetadata,
          type: node.type,
        };
      } else {
        modifiedFieldsMetadata[node.id] = fieldMetadata;
      }
    }
  });

  return modifiedFieldsMetadata;
}

const isSchedulableQuoteByStatus = makeValuesPredicate(['QUOTED', 'QUOTED_EXPIRED']);

export function getBatchSchedulableQuotes(state: AppState, tabId: string): QuoteInfo[] {
  return getAllSelectedFormsMatching(state, tabId, form => !!form.quoteId && isSchedulableQuoteByStatus(form.status))
    .map(([formId]) => {
      const result = getBatchQuoteResult(state, formId);
      const details = getBatchQuoteDetails(state, formId);
      if (!isDefined(result)) {
        return undefined;
      }
      return {
        quoteId: result.quoteExternalId ?? details?.QuoteExternalId,
        productType: details?.ProductType ?? undefined,
        productSubType: details?.ProductSubtype ?? undefined,
      } satisfies QuoteInfo;
    })
    .filter(isDefined);
}
